// What "Nhắc nhở sau tiết học" writes besides the board's own keys: the QLT teacher web's class-note
// fields (lms-fe ClassReminderUpdateDialog) and the one post parents read in their app.

import type { ParentNoticeRequest } from '../../api/repository/class-note';
import type { IztContentNode, QltClassNoteData } from '../../api/types';

export type ParentNoticeType = 'homework' | 'notification';

export interface ReminderUnit {
  id: string;
  name: string;
}

// lms-fe shapes verbatim; its 'exam' checkbox is labelled "Nhắc nhở sau tiết học"
export interface ClassNoteTaskFields {
  taskType: Array<'homework' | 'exam'>;
  otherTaskValue: '';
  iztCourseId: string | null;
  iztLectureId: string[] | null;
}

export interface ParentNotice {
  type: ParentNoticeType;
  title: string;
  content: string;
  shortContent: string;
}

export interface ParentNoticeInput {
  subjectTitle?: string;
  date?: string;
  homeworkNames: string[];
  reminder: string;
}

// stored in class-note data so a re-save with the same content never pushes to parents twice
export interface ParentNoticeRecord {
  postId: string | null;
  sentAt: string;
  digest: string;
  type: ParentNoticeType;
}

export const REMINDER_TITLE = 'Nhắc nhở sau tiết học';
export const HOMEWORK_TITLE = 'Bài tập về nhà';
export const NOTICE_UNRECORDED = 'Đã gửi thông báo cho phụ huynh nhưng chưa lưu được trạng thái gửi vào sổ đầu bài.';
export const NOTICE_UNSUPPORTED = 'Máy chủ chưa hỗ trợ gửi thông báo từ bảng.';

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;

function pad(value: number): string {
  return ('0' + value).slice(-2);
}

function vnClock(iso: string): Date | null {
  const time = Date.parse(iso);
  return isNaN(time) ? null : new Date(time + VN_OFFSET_MS);
}

/** "dd/MM" of a class-note date; stored dates are VN midnight in UTC, so 2026-09-27T17:00:00.000Z is 28/09. */
export function dayMonth(date?: string): string {
  if (!date) return '';
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (bare) return `${bare[3]}/${bare[2]}`;
  const vn = vnClock(date);
  return vn ? `${pad(vn.getUTCDate())}/${pad(vn.getUTCMonth() + 1)}` : '';
}

/** "HH:mm dd/MM" in VN time, for the card's "Phụ huynh" row. */
export function sentLabel(iso: string): string {
  const vn = vnClock(iso);
  return vn ? `${pad(vn.getUTCHours())}:${pad(vn.getUTCMinutes())} ${pad(vn.getUTCDate())}/${pad(vn.getUTCMonth() + 1)}` : '';
}

/** lms-fe's iztCourseId: the tree's first course, "c_"-prefixed; an unloaded tree keeps the stored one. */
export function iztCourseKey(tree: IztContentNode[], previous?: QltClassNoteData): string | null {
  if (tree.length > 0 && tree[0].id) return `c_${tree[0].id}`;
  return previous && typeof previous.iztCourseId === 'string' ? previous.iztCourseId : null;
}

export function classNoteTaskFields(units: ReminderUnit[], reminder: string, courseKey: string | null): ClassNoteTaskFields {
  const homework = units.length > 0;
  const taskType: Array<'homework' | 'exam'> = [];
  if (homework) taskType.push('homework');
  if (reminder.trim()) taskType.push('exam');
  return {
    taskType,
    otherTaskValue: '',
    iztCourseId: homework ? courseKey : null,
    iztLectureId: homework ? units.map((unit) => unit.id) : null,
  };
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** The post parents see, or null when there is nothing to tell them. The parent card renders content as HTML and shows no title. */
export function buildParentNotice(input: ParentNoticeInput): ParentNotice | null {
  const names = input.homeworkNames.map((name) => name.trim()).filter(Boolean);
  const reminder = input.reminder.trim();
  if (names.length === 0 && !reminder) return null;
  const type: ParentNoticeType = names.length > 0 ? 'homework' : 'notification';
  const title = [type === 'homework' ? HOMEWORK_TITLE : REMINDER_TITLE, (input.subjectTitle || '').trim(), dayMonth(input.date)].filter(Boolean).join(' - ');
  const blocks = [`<p><strong>${escapeHtml(title)}</strong></p>`];
  if (names.length > 0) blocks.push(`<ul>${names.map((name) => `<li>${escapeHtml(name)}</li>`).join('')}</ul>`);
  if (reminder) blocks.push(`<p>${names.length > 0 ? 'Nhắc nhở: ' : ''}${escapeHtml(reminder)}</p>`);
  // the push body; lms-api strips tags from content without spacing, which runs the lines together
  const shortContent = names.length > 0 ? `${title}: ${names.join('; ')}${reminder ? `. Nhắc nhở: ${reminder}` : ''}` : `${title}: ${reminder}`;
  return { type, title, content: blocks.join('\n'), shortContent };
}

/** FNV-1a over what parents see; sync so the form needs no crypto.subtle. */
export function noticeDigest(notice: ParentNotice): string {
  const text = `${notice.type}\n${notice.title}\n${notice.content}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return ('0000000' + (hash >>> 0).toString(16)).slice(-8);
}

// lms-api POST /class-notes/:id/parent-notice; the server sets class, postToType, senderMethod and config from the note
export function parentNoticeRequest(notice: ParentNotice): ParentNoticeRequest {
  return { type: notice.type, title: notice.title, content: notice.content, shortContent: notice.shortContent };
}

export function parentNoticeOf(data?: QltClassNoteData): ParentNoticeRecord | null {
  const value = data && data.parentNotice;
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (typeof record.digest !== 'string' || typeof record.sentAt !== 'string') return null;
  return {
    postId: typeof record.postId === 'string' ? record.postId : null,
    sentAt: record.sentAt,
    digest: record.digest,
    type: record.type === 'homework' ? 'homework' : 'notification',
  };
}

export function needsSend(digest: string, previous: ParentNoticeRecord | null): boolean {
  return !previous || previous.digest !== digest;
}

export function noticeRecord(post: unknown, notice: ParentNotice, digest: string, now: Date): ParentNoticeRecord {
  const body = (post && typeof post === 'object' ? post : {}) as { id?: unknown; _id?: unknown };
  const id = body.id !== undefined ? body.id : body._id;
  return { postId: id === undefined || id === null ? null : String(id), sentAt: now.toISOString(), digest, type: notice.type };
}

export interface NoticeFailure {
  kind: string;
  status: number | null;
  data: unknown;
}

/** The route's own Vietnamese reason, which lms-api puts in `data`; null for a body without one. */
export function noticeServerReason(data: unknown): string | null {
  const value = data && typeof data === 'object' ? (data as { data?: unknown }).data : undefined;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// 405, or a 404 without the route's reason, is an lms-api that predates the route; a 404 with one is a missing note
export function noticeFailureMessage(failure: NoticeFailure, fallback: string): string {
  const reason = noticeServerReason(failure.data);
  let why = reason || fallback;
  if (failure.kind === 'forbidden') why = 'tài khoản chưa được cấp quyền gửi thông báo (403).';
  else if (failure.status === 405 || (failure.status === 404 && !reason)) why = NOTICE_UNSUPPORTED;
  return `Đã lưu sổ đầu bài, chưa gửi được thông báo cho phụ huynh: ${why}`;
}
